// Shared client/server calendar contract. Report dates are always India dates.
export const REPORT_ZONE = "Asia/Kolkata";
const DAY = 86_400_000;
const IST = 330 * 60_000;
export type ReportInput = { from?: string; to?: string; days?: number; groupBy?: "day" | "month" };
export type ReportSelection = { from: string; to: string; groupBy: "day" | "month" };

export function indiaDate(value: string | number | Date = new Date()): string {
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) throw new Error("Invalid report timestamp");
  return new Date(time + IST).toISOString().slice(0, 10);
}

export function dateValue(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error("Choose a valid date (YYYY-MM-DD)");
  const time = Date.parse(`${value}T00:00:00Z`);
  if (
    !Number.isFinite(time) ||
    new Date(time).toISOString().slice(0, 10) !== value ||
    value < "2000-01-01" ||
    value > "2100-12-31"
  ) {
    throw new Error("Choose a valid date between 2000 and 2100");
  }
  return time;
}

export function addDays(value: string, days: number): string {
  return new Date(dateValue(value) + days * DAY).toISOString().slice(0, 10);
}

export function monthSelection(month: string): ReportSelection {
  if (!/^\d{4}-\d{2}$/.test(month)) throw new Error("Choose a month and year");
  const first = `${month}-01`;
  dateValue(first);
  const [year, m] = month.split("-").map(Number);
  return {
    from: first,
    to: new Date(Date.UTC(year, m, 0)).toISOString().slice(0, 10),
    groupBy: "day",
  };
}

export function reportPreset(preset: string, now = new Date()): ReportSelection {
  const today = indiaDate(now);
  const month = monthSelection(today.slice(0, 7));
  if (preset === "today") return { from: today, to: today, groupBy: "day" };
  if (preset === "yesterday")
    return { from: addDays(today, -1), to: addDays(today, -1), groupBy: "day" };
  if (preset === "lastMonth") return monthSelection(addDays(month.from, -1).slice(0, 7));
  if (preset === "year") return { from: `${today.slice(0, 4)}-01-01`, to: today, groupBy: "month" };
  if (preset === "12months") {
    const [year, m] = today.split("-").map(Number);
    return {
      from: new Date(Date.UTC(year, m - 12, 1)).toISOString().slice(0, 10),
      to: today,
      groupBy: "month",
    };
  }
  if (preset === "7" || preset === "30" || preset === "90")
    return { from: addDays(today, 1 - Number(preset)), to: today, groupBy: "day" };
  return { from: month.from, to: today, groupBy: "day" };
}

export function resolveReportRange(input: ReportInput = {}, now = new Date()) {
  if (!input || typeof input !== "object" || Array.isArray(input))
    throw new Error("Invalid report range");
  if ((input.from && !input.to) || (!input.from && input.to))
    throw new Error("Choose both From and To dates");
  if (
    input.days !== undefined &&
    (!Number.isInteger(input.days) || input.days < 1 || input.days > 3660)
  )
    throw new Error("Invalid report duration");
  const fallback = input.days
    ? { from: addDays(indiaDate(now), 1 - input.days), to: indiaDate(now), groupBy: "day" as const }
    : reportPreset("month", now);
  const from = input.from ?? fallback.from;
  const to = input.to ?? fallback.to;
  const a = dateValue(from),
    b = dateValue(to);
  const days = (b - a) / DAY + 1;
  if (days < 1) throw new Error("From date must be on or before To date");
  if (days > 3660) throw new Error("Choose a range of 10 years or less");
  const groupBy = input.groupBy ?? "day";
  if (groupBy !== "day" && groupBy !== "month") throw new Error("Invalid report grouping");
  const start = new Date(a - IST).toISOString();
  const end = new Date(b + DAY - IST).toISOString();
  return {
    from,
    to,
    groupBy,
    days,
    start,
    end,
    previousStart: new Date(a - days * DAY - IST).toISOString(),
    previousEnd: start,
    partial: end > now.toISOString(),
  };
}

export function reportSearch(search: Record<string, unknown>) {
  if (typeof search.reportFrom !== "string" || typeof search.reportTo !== "string") return {};
  try {
    const range = resolveReportRange({
      from: search.reportFrom,
      to: search.reportTo,
      groupBy: search.reportGroup === "month" ? "month" : "day",
    });
    return { reportFrom: range.from, reportTo: range.to, reportGroup: range.groupBy };
  } catch {
    return {};
  }
}

export function reportBuckets(range: ReturnType<typeof resolveReportRange>): string[] {
  const buckets = new Set<string>();
  for (let i = 0; i < range.days; i++) {
    const day = addDays(range.from, i);
    buckets.add(range.groupBy === "month" ? day.slice(0, 7) : day);
  }
  return [...buckets];
}

export function reportBucket(timestamp: string, groupBy: "day" | "month") {
  const day = indiaDate(timestamp);
  return groupBy === "month" ? day.slice(0, 7) : day;
}

export function reportLabel(range: { from: string; to: string }): string {
  const format = (date: string) =>
    new Date(`${date}T12:00:00Z`).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: REPORT_ZONE,
    });
  return `${format(range.from)} – ${format(range.to)} · IST`;
}
